import { inject } from '@angular/core';
import { Router, Routes } from '@angular/router';

import { partnerGuard } from './core/guards/partner.guard';
import { adminGuard } from './core/guards/admin.guard';
import { clientAuthGuard } from './core/guards/client.guard';
import { pendingDeletionBlockGuard, pendingDeletionPageGuard } from './core/guards/pending-deletion.guard';
import { maintenanceBlockGuard } from './core/guards/maintenance.guard';

export const routes: Routes = [
  {
    path: '',
    canActivate: [pendingDeletionBlockGuard, maintenanceBlockGuard],
    loadComponent: () => import('./features/client/layout/client-layout.component').then(m => m.ClientLayoutComponent),
    children: [
      {
        path: '',
        data: { animation: 'HomePage' },
        loadComponent: () => import('./features/client/home/client-home.component').then(m => m.ClientHomeComponent)
      },
      {
        path: 'search',
        data: { animation: 'SearchPage' },
        loadComponent: () => import('./features/client/search/search-results.component').then(m => m.SearchResultsComponent)
      },
      {
        path: 'venues',
        data: { animation: 'VenuesSearchPage' },
        loadComponent: () => import('./features/client/venues/venues-search.component').then(m => m.VenuesSearchComponent),
        title: 'ROUTES.CLIENT.VENUES',
      },
      {
        path: 'venues/:id',
        data: { animation: 'VenueDetailsPage' },
        loadComponent: () => import('./features/client/venues/venue-details.component').then(m => m.VenueDetailsComponent),
        title: 'ROUTES.CLIENT.VENUE_DETAIL',
      },
      {
        path: 'establishments',
        data: { animation: 'EstablishmentsPage' },
        loadComponent: () => import('./features/client/establishments/establishments.component').then(m => m.EstablishmentsComponent)
      },
      {
        path: 'establishments/:id',
        data: { animation: 'HotelDetailsPage' },
        loadComponent: () => import('./features/client/hotel-details/hotel-details.component').then(m => m.HotelDetailsComponent)
      },
      {
        path: 'rooms/:id',
        data: { animation: 'RoomDetailsPage' },
        loadComponent: () => import('./features/client/rooms/room-details-page.component').then(m => m.RoomDetailsPageComponent)
      },
      {
        path: 'discover',
        data: { animation: 'DiscoverPage' },
        loadComponent: () => import('./features/client/discover/discover.component').then(m => m.DiscoverComponent)
      },
      {
        path: 'discover/add-activity',
        data: { animation: 'AddActivityPage' },
        loadComponent: () => import('./features/client/discover/add-activity-page.component').then(m => m.AddActivityPageComponent)
      },
      {
        path: 'discover/submit',
        data: { animation: 'AddActivityPage' },
        loadComponent: () => import('./features/client/discover/add-activity-page.component').then(m => m.AddActivityPageComponent)
      },
      {
        path: 'discover/activity-stats',
        data: { animation: 'ActivityStatsPage' },
        loadComponent: () => import('./features/client/discover/activity-stats-page.component').then(m => m.ActivityStatsPageComponent)
      },
      {
        path: 'discover/:id/stats',
        data: { animation: 'ActivityStatsPage' },
        loadComponent: () => import('./features/client/discover/activity-stats-page.component').then(m => m.ActivityStatsPageComponent)
      },
      {
        path: 'discover/:id',
        data: { animation: 'DiscoverPage' },
        loadComponent: () => import('./features/client/discover/discover.component').then(m => m.DiscoverComponent)
      },
      {
        path: 'activities',
        data: { animation: 'DiscoverPage' },
        loadComponent: () => import('./features/client/discover/discover.component').then(m => m.DiscoverComponent)
      },
      {
        path: 'activities/submit',
        data: { animation: 'AddActivityPage' },
        loadComponent: () => import('./features/client/discover/add-activity-page.component').then(m => m.AddActivityPageComponent)
      },
      {
        path: 'activities/stats',
        data: { animation: 'ActivityStatsPage' },
        loadComponent: () => import('./features/client/discover/activity-stats-page.component').then(m => m.ActivityStatsPageComponent)
      },
      {
        path: 'activities/:id/stats',
        data: { animation: 'ActivityStatsPage' },
        loadComponent: () => import('./features/client/discover/activity-stats-page.component').then(m => m.ActivityStatsPageComponent)
      },
      {
        path: 'activities/:id',
        data: { animation: 'DiscoverPage' },
        loadComponent: () => import('./features/client/discover/discover.component').then(m => m.DiscoverComponent)
      },
      {
        path: 'help-center',
        data: { animation: 'HelpCenterPage' },
        loadComponent: () => import('./features/client/help-center/help-center.component').then(m => m.HelpCenterComponent)
      },
      {
        path: 'assistant',
        data: { animation: 'AssistantPage' },
        title: 'ROUTES.CLIENT.ASSISTANT',
        loadComponent: () => import('./features/client/assistant/assistant.component').then(m => m.AssistantComponent)
      },
      {
        path: 'booking',
        canActivate: [clientAuthGuard],
        loadComponent: () => import('./features/client/booking/booking-form.component').then(m => m.BookingFormComponent)
      },
      {
        path: 'payment/callback',
        data: { animation: 'PaymentCallbackPage' },
        loadComponent: () => import('./features/client/payment-callback/payment-callback.component').then(m => m.PaymentCallbackComponent)
      },
      // FlexPay card return status pages (approved / cancelled / declined).
      {
        path: 'payment/callback/approved',
        data: { animation: 'PaymentCallbackPage' },
        loadComponent: () => import('./features/client/payment-callback/payment-callback.component').then(m => m.PaymentCallbackComponent)
      },
      {
        path: 'payment/callback/cancelled',
        data: { animation: 'PaymentCallbackPage' },
        loadComponent: () => import('./features/client/payment-callback/payment-callback.component').then(m => m.PaymentCallbackComponent)
      },
      {
        path: 'payment/callback/canceled',
        data: { animation: 'PaymentCallbackPage' },
        loadComponent: () => import('./features/client/payment-callback/payment-callback.component').then(m => m.PaymentCallbackComponent)
      },
      {
        path: 'payment/callback/declined',
        data: { animation: 'PaymentCallbackPage' },
        loadComponent: () => import('./features/client/payment-callback/payment-callback.component').then(m => m.PaymentCallbackComponent)
      },
      {
        path: 'booking/confirmation',
        data: { animation: 'PaymentCallbackPage' },
        loadComponent: () => import('./features/client/payment-callback/payment-callback.component').then(m => m.PaymentCallbackComponent)
      },
      {
        path: 'client/profile',
        canActivate: [clientAuthGuard],
        data: { animation: 'UserProfilePage' },
        loadComponent: () => import('./features/client/profile/user-profile.component').then(m => m.UserProfileComponent)
      },
      {
        path: 'client/bookings/:id',
        canActivate: [clientAuthGuard],
        data: { animation: 'BookingDetailPage' },
        loadComponent: () => import('./features/client/bookings/booking-detail.component').then(m => m.BookingDetailComponent)
      },
      {
        path: 'venue-quotes/:id',
        data: { animation: 'VenueQuoteDetailPage' },
        loadComponent: () => import('./features/client/venues/venue-quote-detail.component').then(m => m.VenueQuoteDetailComponent),
        title: 'ROUTES.CLIENT.VENUE_QUOTE',
      },
      {
        path: 'client/venue-quotes/:id',
        data: { animation: 'VenueQuoteDetailPage' },
        loadComponent: () => import('./features/client/venues/venue-quote-detail.component').then(m => m.VenueQuoteDetailComponent),
        title: 'ROUTES.CLIENT.VENUE_QUOTE',
      },
      {
        path: 'client/notifications',
        canActivate: [clientAuthGuard],
        data: { animation: 'ClientNotificationsPage' },
        loadComponent: () => import('./features/client/notifications/notifications.component').then(m => m.ClientNotificationsComponent)
      },
      {
        path: 'client/support',
        canActivate: [clientAuthGuard],
        data: { animation: 'ClientSupportPage' },
        title: 'ROUTES.CLIENT.SUPPORT',
        loadComponent: () => import('./features/support/user-support-ticket-list.component').then(m => m.UserSupportTicketListComponent)
      },
      {
        path: 'client/support/:id',
        canActivate: [clientAuthGuard],
        data: { animation: 'ClientSupportDetailPage' },
        title: 'ROUTES.CLIENT.SUPPORT_DETAIL',
        loadComponent: () => import('./features/support/user-support-ticket-detail.component').then(m => m.UserSupportTicketDetailComponent)
      },
      {
        path: 'terms',
        title: 'ROUTES.LEGAL.TERMS',
        loadComponent: () => import('./features/client/legal/terms.component').then(m => m.TermsComponent)
      },
      {
        path: 'conditions-d-utilisation',
        redirectTo: 'terms',
        pathMatch: 'full'
      },
      {
        path: 'privacy',
        title: 'ROUTES.LEGAL.PRIVACY',
        loadComponent: () => import('./features/client/legal/privacy.component').then(m => m.PrivacyComponent)
      },
      {
        path: 'politique-de-confidentialite',
        redirectTo: 'privacy',
        pathMatch: 'full'
      },
      {
        path: 'cgv',
        title: 'ROUTES.LEGAL.SALES',
        loadComponent: () => import('./features/client/legal/cgv.component').then(m => m.CgvComponent)
      },
      {
        path: 'conditions-generales-de-vente',
        redirectTo: 'cgv',
        pathMatch: 'full'
      },
      {
        path: 'legal',
        title: 'ROUTES.LEGAL.NOTICE',
        loadComponent: () => import('./features/client/legal/legal.component').then(m => m.LegalComponent)
      },
      {
        path: 'mentions-legales',
        redirectTo: 'legal',
        pathMatch: 'full'
      }
    ]
  },
  {
    path: 'auth/become-partner',
    loadComponent: () => import('./features/auth/become-partner/become-partner.component').then(m => m.BecomePartnerComponent)
  },
  {
    // Top-level (like become-partner) so OAuth returns never miss a nested child match.
    path: 'auth/google/callback',
    data: { animation: 'GoogleCallbackPage' },
    loadComponent: () => import('./features/auth/google-callback/google-callback.component').then(m => m.GoogleCallbackComponent)
  },
  {
    path: 'verify-email/:id/:hash',
    loadComponent: () => import('./features/auth/verify-email/verify-email.component').then(m => m.VerifyEmailComponent)
  },
  {
    path: 'verify-email',
    loadComponent: () => import('./features/auth/verify-email/verify-email.component').then(m => m.VerifyEmailComponent)
  },
  {
    path: 'auth/pending-deletion',
    data: { animation: 'PendingDeletionPage' },
    canActivate: [pendingDeletionPageGuard],
    loadComponent: () => import('./features/auth/pending-deletion/pending-deletion.component').then(m => m.PendingDeletionComponent)
  },
  {
    path: 'pending-deletion',
    redirectTo: 'auth/pending-deletion',
    pathMatch: 'full'
  },
  {
    path: 'auth',
    loadComponent: () => import('./features/auth/layout/auth-layout.component').then(m => m.AuthLayoutComponent),
    children: [
      { path: 'login', data: { animation: 'LoginPage' }, loadComponent: () => import('./features/auth/login/login.component').then(m => m.LoginComponent) },
      { path: 'register', data: { animation: 'RegisterPage' }, loadComponent: () => import('./features/auth/register/register.component').then(m => m.RegisterComponent) },
      { path: 'forgot-password', data: { animation: 'ForgotPasswordPage' }, loadComponent: () => import('./features/auth/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent) },
      { path: 'reset-password', data: { animation: 'ResetPasswordPage' }, loadComponent: () => import('./features/auth/reset-password/reset-password.component').then(m => m.ResetPasswordComponent) },
      { path: 'two-factor/reset-confirm', data: { animation: 'TwoFactorResetPage' }, loadComponent: () => import('./features/auth/two-factor-reset-confirm/two-factor-reset-confirm.component').then(m => m.TwoFactorResetConfirmComponent) },
      { path: 'two-factor/reset/:id/:hash', data: { animation: 'TwoFactorResetPage' }, loadComponent: () => import('./features/auth/two-factor-reset-confirm/two-factor-reset-confirm.component').then(m => m.TwoFactorResetConfirmComponent) },
      { path: 'verify-email/:id/:hash', loadComponent: () => import('./features/auth/verify-email/verify-email.component').then(m => m.VerifyEmailComponent) },
      { path: 'verify-email', loadComponent: () => import('./features/auth/verify-email/verify-email.component').then(m => m.VerifyEmailComponent) },
      { path: '', redirectTo: 'login', pathMatch: 'full' }
    ]
  },
  {
    path: 'manager',
    loadComponent: () =>
      import('./features/manager/layout/layout.component').then((m) => m.ManagerLayoutComponent),
    canActivate: [partnerGuard],
    children: [
      { path: 'pending-approval', loadComponent: () => import('./features/manager/pending-approval/pending-approval.component').then(m => m.ManagerPendingApprovalComponent) },
      { path: 'dashboard', loadComponent: () => import('./features/manager/dashboard/dashboard.component').then(m => m.ManagerDashboardComponent), data: { animation: 'ManagerDashboard' } },
      {
        path: 'hotels',
        data: { animation: 'ManagerHotels' },
        loadComponent: () => import('./features/manager/hotels/hotel-list/hotel-list.component').then(m => m.HotelListComponent)
      },
      {
        path: 'hotels/new',
        loadComponent: () => import('./features/manager/hotels/hotel-form/hotel-form.component').then(m => m.HotelFormComponent)
      },
      {
        path: 'hotels/:id',
        title: 'ROUTES.MANAGER.ESTABLISHMENT_DETAIL',
        loadComponent: () => import('./features/manager/hotels/hotel-detail/hotel-detail.component').then(m => m.HotelDetailComponent)
      },
      {
        path: 'hotels/:id/edit',
        title: 'ROUTES.MANAGER.ESTABLISHMENT_EDIT',
        loadComponent: () => import('./features/manager/hotels/hotel-form/hotel-form.component').then(m => m.HotelFormComponent)
      },
      {
        path: 'hotels/:id/rooms/new',
        title: 'ROUTES.MANAGER.ROOM_CREATE',
        loadComponent: () => import('./features/manager/hotels/room-form/room-form.component').then(m => m.RoomFormComponent)
      },
      {
        path: 'hotels/:id/rooms/:roomId/edit',
        title: 'ROUTES.MANAGER.ROOM_EDIT',
        loadComponent: () => import('./features/manager/hotels/room-form/room-form.component').then(m => m.RoomFormComponent)
      },
      {
        path: 'reservations',
        loadComponent: () => import('./features/manager/bookings/booking-list.component').then(m => m.BookingListComponent)
      },
      {
        path: 'payments',
        title: 'ROUTES.MANAGER.PAYMENTS',
        loadComponent: () => import('./features/manager/payments/payments.component').then(m => m.ManagerPaymentsComponent)
      },
      {
        path: 'reviews',
        title: 'ROUTES.MANAGER.REVIEWS',
        loadComponent: () => import('./features/manager/reviews/reviews.component').then(m => m.ManagerReviewsComponent)
      },
      {
        path: 'promotions',
        title: 'ROUTES.MANAGER.PROMOTIONS',
        loadComponent: () => import('./features/manager/promotions/promotions.component').then(m => m.ManagerPromotionsComponent)
      },
      {
        path: 'commissions',
        title: 'ROUTES.MANAGER.COMMISSIONS',
        loadComponent: () => import('./features/manager/commissions/commissions.component').then(m => m.ManagerCommissionsComponent)
      },
      {
        path: 'revenues',
        redirectTo: 'payments',
        pathMatch: 'full'
      },
      {
        path: 'notifications',
        title: 'ROUTES.MANAGER.NOTIFICATIONS',
        loadComponent: () => import('./features/manager/notifications/notifications.component').then(m => m.ManagerNotificationsComponent)
      },
      {
        path: 'support',
        title: 'ROUTES.MANAGER.SUPPORT',
        loadComponent: () => import('./features/support/user-support-ticket-list.component').then(m => m.UserSupportTicketListComponent)
      },
      {
        path: 'support/:id',
        title: 'ROUTES.MANAGER.SUPPORT_DETAIL',
        loadComponent: () => import('./features/support/user-support-ticket-detail.component').then(m => m.UserSupportTicketDetailComponent)
      },
      {
        path: 'venues',
        title: 'ROUTES.MANAGER.VENUES',
        loadComponent: () => import('./features/manager/venues/partner-venue-list.component').then(m => m.PartnerVenueListComponent)
      },
      {
        path: 'venue-quotes',
        title: 'ROUTES.MANAGER.VENUE_QUOTES',
        loadComponent: () => import('./features/manager/venues/partner-venue-quotes.component').then(m => m.PartnerVenueQuotesComponent)
      },
      {
        path: 'venue-quotes/:id',
        title: 'ROUTES.MANAGER.VENUE_QUOTE_REPLY',
        loadComponent: () => import('./features/manager/venues/partner-venue-quote-detail.component').then(m => m.PartnerVenueQuoteDetailComponent)
      },
      {
        path: 'venues/new',
        title: 'ROUTES.MANAGER.VENUE_CREATE',
        loadComponent: () => import('./features/manager/venues/partner-venue-form.component').then(m => m.PartnerVenueFormComponent)
      },
      {
        path: 'venues/:id/edit',
        title: 'ROUTES.MANAGER.VENUE_EDIT',
        loadComponent: () => import('./features/manager/venues/partner-venue-form.component').then(m => m.PartnerVenueFormComponent)
      },
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' }
    ]
  },
  {
    path: 'admin',
    loadComponent: () =>
      import('./features/admin/layout/admin-layout.component').then((m) => m.AdminLayoutComponent),
    canActivate: [adminGuard],
    children: [
      { path: 'dashboard', loadComponent: () => import('./features/admin/dashboard/admin-dashboard.component').then(m => m.AdminDashboardComponent), title: 'ROUTES.ADMIN.DASHBOARD', data: { animation: 'AdminDashboard' } },
      { path: 'users', loadComponent: () => import('./features/admin/users/user-list.component').then(m => m.UserListComponent), title: 'ROUTES.ADMIN.USERS', data: { animation: 'AdminUsers' } },
      { path: 'establishments', loadComponent: () => import('./features/admin/establishments/establishment-list.component').then(m => m.EstablishmentListComponent), title: 'ROUTES.ADMIN.ESTABLISHMENTS', data: { animation: 'AdminEst' } },
      { path: 'establishments/:id', loadComponent: () => import('./features/admin/establishments/establishment-detail.component').then(m => m.EstablishmentDetailComponent), title: 'ROUTES.ADMIN.ESTABLISHMENT_DETAIL', data: { animation: 'AdminEstDetail' } },
      { path: 'venues', loadComponent: () => import('./features/admin/venues/admin-venues.component').then(m => m.AdminVenuesComponent), title: 'ROUTES.ADMIN.VENUES', data: { animation: 'AdminVenues' } },
      { path: 'venue-quotes', pathMatch: 'full', redirectTo: () => inject(Router).createUrlTree(['/admin/venues'], { queryParams: { tab: 'devis' } }) },
      { path: 'venue-quotes/:id', loadComponent: () => import('./features/admin/venues/admin-venue-quote-detail.component').then(m => m.AdminVenueQuoteDetailComponent), title: 'ROUTES.ADMIN.VENUE_QUOTE', data: { animation: 'AdminVenueQuoteDetail' } },
      { path: 'activities', loadComponent: () => import('./features/admin/activities/admin-activities-list.component').then(m => m.AdminActivitiesListComponent), title: 'ROUTES.ADMIN.ACTIVITIES', data: { animation: 'AdminActivities' } },
      { path: 'activities/:id', loadComponent: () => import('./features/admin/activities/admin-activity-detail.component').then(m => m.AdminActivityDetailComponent), title: 'ROUTES.ADMIN.ACTIVITY_DETAIL', data: { animation: 'AdminActivityDetail' } },
      { path: 'bookings', loadComponent: () => import('./features/admin/bookings/admin-bookings.component').then(m => m.AdminBookingsComponent), title: 'ROUTES.ADMIN.BOOKINGS', data: { animation: 'AdminBookings' } },
      { path: 'services', loadComponent: () => import('./features/admin/services/admin-services.component').then(m => m.AdminServicesComponent), title: 'ROUTES.ADMIN.SERVICES', data: { animation: 'AdminServices' } },
      { path: 'room-services', loadComponent: () => import('./features/admin/room-services/admin-room-services.component').then(m => m.AdminRoomServicesComponent), title: 'ROUTES.ADMIN.ROOM_SERVICES', data: { animation: 'AdminRoomServices' } },
      { path: 'commissions', loadComponent: () => import('./features/admin/commissions/admin-commissions.component').then(m => m.AdminCommissionsComponent), title: 'ROUTES.ADMIN.COMMISSIONS', data: { animation: 'AdminCommissions' } },
      { path: 'demandes', loadComponent: () => import('./features/admin/requests/admin-establishment-requests.component').then(m => m.AdminEstablishmentRequestsComponent), title: 'ROUTES.ADMIN.REQUESTS', data: { animation: 'AdminRequests' } },
      { path: 'verifications', loadComponent: () => import('./features/admin/verifications/admin-verifications.component').then(m => m.AdminVerificationsComponent), title: 'ROUTES.ADMIN.VERIFICATIONS', data: { animation: 'AdminVerifications' } },
      { path: 'payments', loadComponent: () => import('./features/admin/payments/admin-payments.component').then(m => m.AdminPaymentsComponent), title: 'ROUTES.ADMIN.PAYMENTS', data: { animation: 'AdminPayments' } },
      { path: 'payouts', loadComponent: () => import('./features/admin/payouts/admin-payouts.component').then(m => m.AdminPayoutsComponent), title: 'ROUTES.ADMIN.PAYOUTS', data: { animation: 'AdminPayouts' } },
      { path: 'availabilities', loadComponent: () => import('./features/admin/availabilities/admin-availabilities.component').then(m => m.AdminAvailabilitiesComponent), title: 'ROUTES.ADMIN.AVAILABILITIES', data: { animation: 'AdminAvailabilities' } },
      { path: 'analytics', loadComponent: () => import('./features/admin/analytics/admin-analytics.component').then(m => m.AdminAnalyticsComponent), title: 'ROUTES.ADMIN.ANALYTICS', data: { animation: 'AdminAnalytics' } },
      { path: 'support', loadComponent: () => import('./features/admin/support/admin-support.component').then(m => m.AdminSupportComponent), title: 'ROUTES.ADMIN.SUPPORT', data: { animation: 'AdminSupport' } },
      { path: 'disputes', loadComponent: () => import('./features/admin/disputes/admin-disputes.component').then(m => m.AdminDisputesComponent), title: 'ROUTES.ADMIN.DISPUTES', data: { animation: 'AdminDisputes' } },
      { path: 'refunds', loadComponent: () => import('./features/admin/refunds/admin-refunds.component').then(m => m.AdminRefundsComponent), title: 'ROUTES.ADMIN.REFUNDS', data: { animation: 'AdminRefunds' } },
      { path: 'content', loadComponent: () => import('./features/admin/content/admin-content.component').then(m => m.AdminContentComponent), title: 'ROUTES.ADMIN.CONTENT', data: { animation: 'AdminContent' } },
      { path: 'rooms', loadComponent: () => import('./features/admin/rooms/admin-rooms.component').then(m => m.AdminRoomsComponent), title: 'ROUTES.ADMIN.ROOMS', data: { animation: 'AdminRooms' } },
      { path: 'marketing', loadComponent: () => import('./features/admin/marketing/admin-marketing.component').then(m => m.AdminMarketingComponent), title: 'ROUTES.ADMIN.MARKETING', data: { animation: 'AdminMarketing' } },
      { path: 'audit', loadComponent: () => import('./features/admin/audit-logs/admin-audit-logs.component').then(m => m.AdminAuditLogsComponent), title: 'ROUTES.ADMIN.AUDIT', data: { animation: 'AdminAudit' } },
      { path: 'emails', loadComponent: () => import('./features/admin/emails/admin-email-broadcast.component').then(m => m.AdminEmailBroadcastComponent), title: 'ROUTES.ADMIN.EMAILS', data: { animation: 'AdminEmails' } },
      { path: 'settings', loadComponent: () => import('./features/admin/settings/admin-settings.component').then(m => m.AdminSettingsComponent), title: 'ROUTES.ADMIN.SETTINGS', data: { animation: 'AdminSettings' } },
      { path: 'reviews', loadComponent: () => import('./features/admin/reviews/admin-reviews.component').then(m => m.AdminReviewsComponent), title: 'ROUTES.ADMIN.REVIEWS', data: { animation: 'AdminReviews' } },
      { path: 'moderation/images', loadComponent: () => import('./features/admin/moderation/admin-image-moderation.component').then(m => m.AdminImageModerationComponent), title: 'ROUTES.ADMIN.MODERATION', data: { animation: 'AdminImageModeration' } },
      { path: 'notifications', loadComponent: () => import('./features/admin/notifications/notifications.component').then(m => m.AdminNotificationsComponent), title: 'ROUTES.ADMIN.NOTIFICATIONS', data: { animation: 'AdminNotif' } },
      { path: 'profile', loadComponent: () => import('./features/admin/profile/admin-profile.component').then(m => m.AdminProfileComponent), title: 'ROUTES.ADMIN.PROFILE', data: { animation: 'AdminProfile' } },
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' }
    ]
  },
  {
    path: '404',
    loadComponent: () => import('./features/errors/not-found/not-found.component').then(m => m.NotFoundComponent)
  },
  {
    path: 'access-denied',
    loadComponent: () => import('./features/errors/access-denied/access-denied.component').then(m => m.AccessDeniedComponent)
  },
  {
    path: '500',
    loadComponent: () => import('./features/errors/server-error/server-error.component').then(m => m.ServerErrorComponent)
  },
  {
    path: 'offline',
    loadComponent: () => import('./features/errors/offline/offline.component').then(m => m.OfflineComponent)
  },
  {
    path: 'maintenance',
    loadComponent: () =>
      import('./features/errors/maintenance/maintenance.component').then((m) => m.MaintenanceComponent),
    title: 'ROUTES.MAINTENANCE',
  },
  {
    path: '**',
    redirectTo: '404'
  }
];
